import React, { useState, useEffect } from 'react';
import calibrationService, { CalibrationTable, CreateCalibrationTableRequest, UpdateCalibrationTableRequest, CalibrationTableSearchRequest, PaginatedResponse } from '../services/calibrationService';
import carService from '../services/carService';
import CalibrationTableModal from './CalibrationTableModal';
import './CalibrationTableManagement.css';

interface CalibrationTableManagementProps {
  onClose?: () => void;
}

const CalibrationTableManagement: React.FC<CalibrationTableManagementProps> = ({ onClose }) => {
  const [tables, setTables] = useState<CalibrationTable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingTable, setEditingTable] = useState<CalibrationTable | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCar, setSelectedCar] = useState<string>('');
  // Убираем фильтр по типу датчика - используем только FLS485
  // const [selectedSensorType, setSelectedSensorType] = useState<string>('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [sortBy, setSortBy] = useState<string>('id');
  const [sortDir, setSortDir] = useState<'ASC' | 'DESC'>('ASC');
  const [selectedTables, setSelectedTables] = useState<number[]>([]);
  const [cars, setCars] = useState<any[]>([]);

  const sensorTypes = calibrationService.getSensorTypes();

  const sortOptions = [
    { value: 'id', label: 'ID' },
    { value: 'name', label: 'Название' },
    { value: 'carName', label: 'Машина' },
    { value: 'sensorNumber', label: 'Номер датчика' },
    { value: 'sensorType', label: 'Тип датчика' },
    { value: 'createdAt', label: 'Дата создания' }
  ];

  useEffect(() => {
    fetchTables();
    fetchCars();
  }, [currentPage, pageSize, sortBy, sortDir]);

  const fetchTables = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: PaginatedResponse<CalibrationTable> = await calibrationService.getCalibrationTables(currentPage, pageSize, sortBy, sortDir);
      setTables(response.content);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
    } catch (err) {
      console.error('Error fetching calibration tables:', err);
      setError('Ошибка при загрузке тарировочных таблиц');
    } finally {
      setLoading(false);
    }
  };

  const fetchCars = async () => {
    try {
      console.log('CalibrationTableManagement: Загружаем машины...');
      const carsResponse = await carService.getCars(0, 1000);
      console.log('CalibrationTableManagement: Получены машины:', carsResponse);
      setCars(carsResponse.content);
      console.log('CalibrationTableManagement: Установлены машины:', carsResponse.content);
    } catch (err) {
      console.error('Error fetching cars:', err);
    }
  };

  const handleCreateTable = async (data: CreateCalibrationTableRequest) => {
    try {
      await calibrationService.createCalibrationTable(data);
      setIsCreateModalOpen(false);
      fetchTables();
      alert('Тарировочная таблица успешно создана!');
    } catch (err: any) {
      console.error('Error creating calibration table:', err);
      alert(err.message || 'Ошибка при создании тарировочной таблицы');
    }
  };

  const handleUpdateTable = async (id: number, data: UpdateCalibrationTableRequest) => {
    try {
      await calibrationService.updateCalibrationTable(id, data);
      setEditingTable(null);
      fetchTables();
      alert('Тарировочная таблица успешно обновлена!');
    } catch (err: any) {
      console.error('Error updating calibration table:', err);
      alert(err.message || 'Ошибка при обновлении тарировочной таблицы');
    }
  };

  const handleDeleteTable = async (table: CalibrationTable) => {
    if (!window.confirm(`Вы уверены, что хотите удалить таблицу "${table.name}"?`)) {
      return;
    }

    try {
      await calibrationService.deleteCalibrationTable(table.id);
      fetchTables();
      alert('Тарировочная таблица успешно удалена!');
    } catch (err: any) {
      console.error('Error deleting calibration table:', err);
      alert(err.message || 'Ошибка при удалении тарировочной таблицы');
    }
  };

  const handleToggleTable = async (table: CalibrationTable) => {
    try {
      await calibrationService.toggleCalibrationTable(table.id);
      fetchTables();
      alert(`Тарировочная таблица "${table.name}" ${table.isActive ? 'деактивирована' : 'активирована'}!`);
    } catch (err: any) {
      console.error('Error toggling calibration table:', err);
      alert(err.message || 'Ошибка при изменении статуса таблицы');
    }
  };

  const handleBulkAction = async (action: 'activate' | 'deactivate' | 'delete') => {
    if (selectedTables.length === 0) {
      alert('Выберите таблицы для выполнения действия');
      return;
    }

    const confirmMessage = {
      activate: `Активировать ${selectedTables.length} таблиц?`,
      deactivate: `Деактивировать ${selectedTables.length} таблиц?`,
      delete: `Удалить ${selectedTables.length} таблиц?`
    };

    if (!window.confirm(confirmMessage[action])) {
      return;
    }

    try {
      for (const tableId of selectedTables) {
        switch (action) {
          case 'activate':
            await calibrationService.toggleCalibrationTable(tableId);
            break;
          case 'deactivate':
            await calibrationService.toggleCalibrationTable(tableId);
            break;
          case 'delete':
            await calibrationService.deleteCalibrationTable(tableId);
            break;
        }
      }
      setSelectedTables([]);
      fetchTables();
      alert(`Действие выполнено для ${selectedTables.length} таблиц`);
    } catch (err) {
      console.error('Error performing bulk action:', err);
      alert('Ошибка при выполнении действия');
    }
  };

  const handleSelectTable = (tableId: number) => {
    setSelectedTables(prev => 
      prev.includes(tableId) 
        ? prev.filter(id => id !== tableId)
        : [...prev, tableId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTables.length === filteredTables.length) {
      setSelectedTables([]);
    } else {
      setSelectedTables(filteredTables.map(table => table.id));
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortDir('ASC');
    }
  };

  const openEditModal = (table: CalibrationTable) => {
    setEditingTable(table);
  };

  const closeEditModal = () => {
    setEditingTable(null);
  };

  const filteredTables = tables.filter(table => {
    const matchesSearch = 
      table.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      table.carName.toLowerCase().includes(searchTerm.toLowerCase());
    // Убираем проверку на активность
    // const matchesActive = !showActiveOnly || table.isActive;
    const matchesCar = !selectedCar || table.carId.toString() === selectedCar;
    // Убираем фильтр по типу датчика - используем только FLS485
    // const matchesSensorType = !selectedSensorType || table.sensorType === selectedSensorType;
    return matchesSearch && matchesCar;
  });

  if (loading) {
    return (
      <div className="calibration-management">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Загрузка тарировочных таблиц...</p>
        </div>
      </div>
    );
  }

  console.log('CalibrationTableManagement рендер. Машины:', cars);
  console.log('Выбранная машина в фильтре:', selectedCar);

  return (
    <div className="calibration-management">
      <div className="calibration-header">
        <div className="header-left">
          <h2>Управление тарировочными таблицами</h2>
          <div className="quick-stats">
            <span className="stat-item">
              <strong>{totalElements}</strong> всего
            </span>
            {/* Убираем статистику по активным/неактивным таблицам */}
            {/* <span className="stat-item">
              <strong>{tables.filter(t => t.isActive).length}</strong> активных
            </span>
            <span className="stat-item">
              <strong>{tables.filter(t => !t.isActive).length}</strong> неактивных
            </span> */}
          </div>
        </div>
        <div className="calibration-actions">
          <button 
            onClick={() => setIsCreateModalOpen(true)} 
            className="create-table-btn"
          >
            Создать таблицу
          </button>
          <div className="bulk-actions">
            {/* Убираем массовые действия активации/деактивации */}
            {/* <button onClick={() => handleBulkAction('activate')} className="bulk-activate-btn">
              Активировать выбранных
            </button>
            <button onClick={() => handleBulkAction('deactivate')} className="bulk-deactivate-btn">
              Деактивировать выбранных
            </button> */}
            <button onClick={() => handleBulkAction('delete')} className="bulk-delete-btn">
              Удалить выбранных
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchTables} className="retry-button">
            Попробовать снова
          </button>
        </div>
      )}

      <div className="calibration-filters">
        <div className="search-section">
          <input
            type="text"
            placeholder="Поиск по названию, машине или типу датчика..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-section">
          <select
            value={selectedCar}
            onChange={(e) => setSelectedCar(e.target.value)}
            className="car-filter"
          >
            <option value="">Все машины ({cars.length})</option>
            {cars.map(car => (
              <option key={car.id} value={car.id.toString()}>{car.name}</option>
            ))}
          </select>
          {/* Убираем фильтр по типу датчика - используем только FLS485 */}
          {/* <select
            value={selectedSensorType}
            onChange={(e) => setSelectedSensorType(e.target.value)}
            className="sensor-type-filter"
          >
            <option value="">Все типы датчиков</option>
            {sensorTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select> */}
          {/* Убираем фильтр "Только активные" */}
          {/* <label className="filter-checkbox">
            <input
              type="checkbox"
              checked={showActiveOnly}
              onChange={(e) => setShowActiveOnly(e.target.checked)}
            />
            Только активные
          </label> */}
        </div>
        <div className="view-controls">
          <button onClick={() => setViewMode(viewMode === 'cards' ? 'table' : 'cards')} className="view-toggle-btn">
            {viewMode === 'cards' ? 'Список' : 'Карточки'}
          </button>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="page-size-select"
          >
            <option value={5}>5 на странице</option>
            <option value={10}>10 на странице</option>
            <option value={20}>20 на странице</option>
            <option value={50}>50 на странице</option>
          </select>
        </div>
      </div>

      <div className="calibration-tables-list">
        {viewMode === 'cards' ? (
          filteredTables.map((table) => (
            <div key={table.id} className="calibration-table-card">
              <div className="table-info">
                <div className="table-header-info">
                  <div className="table-name">{table.name}</div>
                  {/* Убираем отображение статуса */}
                  {/* <span 
                    className={`table-status-badge ${table.isActive ? 'active' : 'inactive'}`}
                  >
                    {table.isActive ? 'Активна' : 'Неактивна'}
                  </span> */}
                </div>
                <div className="table-details">
                  <p><strong>Машина:</strong> {table.carName}</p>
                  <p><strong>Датчик:</strong> №{table.sensorNumber}</p>
                  <p><strong>Точек калибровки:</strong> {table.calibrationData.length}</p>
                </div>
                <div className="table-meta">
                  <span>Создана: {new Date(table.createdAt).toLocaleDateString('ru-RU')}</span>
                  {table.updatedAt && <span>Обновлена: {new Date(table.updatedAt).toLocaleDateString('ru-RU')}</span>}
                </div>
              </div>
              <div className="table-actions">
                <button 
                  onClick={() => openEditModal(table)} 
                  className="edit-btn"
                >
                  Редактировать
                </button>
                {/* Убираем кнопки активации/деактивации */}
                {/* <button 
                  onClick={() => handleToggleTable(table)} 
                  className={`toggle-btn ${table.isActive ? 'deactivate' : 'activate'}`}
                >
                  {table.isActive ? 'Деактивировать' : 'Активировать'}
                </button> */}
                <button 
                  onClick={() => handleDeleteTable(table)} 
                  className="delete-btn"
                >
                  Удалить
                </button>
              </div>
            </div>
          ))
        ) : (
          <table className="calibration-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedTables.length === filteredTables.length && filteredTables.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th onClick={() => handleSort('id')} className="sortable">
                  ID {sortBy === 'id' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('name')} className="sortable">
                  Название {sortBy === 'name' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('carName')} className="sortable">
                  Машина {sortBy === 'carName' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('sensorNumber')} className="sortable">
                  Датчик {sortBy === 'sensorNumber' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                {/* Убираем колонку типа датчика - используем только FLS485 */}
                {/* <th onClick={() => handleSort('sensorType')} className="sortable">
                  Тип датчика {sortBy === 'sensorType' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th> */}
                <th>Точек</th>
                {/* Убираем колонку статуса */}
                {/* <th>Статус</th> */}
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredTables.map((table) => (
                <tr key={table.id} className="table-row">
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedTables.includes(table.id)}
                      onChange={() => handleSelectTable(table.id)}
                    />
                  </td>
                  <td>{table.id}</td>
                  <td>{table.name}</td>
                  <td>{table.carName}</td>
                  <td>№{table.sensorNumber}</td>
                  {/* Убираем отображение типа датчика - используем только FLS485 */}
                  {/* <td>{table.sensorType}</td> */}
                  <td>{table.calibrationData.length}</td>
                  {/* Убираем отображение статуса */}
                  {/* <td>
                    <span 
                      className={`table-status-badge ${table.isActive ? 'active' : 'inactive'}`}
                    >
                      {table.isActive ? 'Активна' : 'Неактивна'}
                    </span>
                  </td> */}
                  <td>
                    <button 
                      onClick={() => openEditModal(table)} 
                      className="edit-btn"
                    >
                      Редактировать
                    </button>
                    {/* Убираем кнопки активации/деактивации */}
                    {/* <button 
                      onClick={() => handleToggleTable(table)} 
                      className={`toggle-btn ${table.isActive ? 'deactivate' : 'activate'}`}
                    >
                      {table.isActive ? 'Деактивировать' : 'Активировать'}
                    </button> */}
                    <button 
                      onClick={() => handleDeleteTable(table)} 
                      className="delete-btn"
                    >
                      Удалить
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {filteredTables.length === 0 && !loading && (
        <div className="empty-state">
          <p>Тарировочные таблицы не найдены</p>
        </div>
      )}

      {/* Пагинация */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <button onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0}>Назад</button>
          <span>Страница {currentPage + 1} из {totalPages}</span>
          <button onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))} disabled={currentPage === totalPages - 1}>Вперед</button>
        </div>
      )}

      {/* Модальное окно создания */}
      {isCreateModalOpen && (
        <CalibrationTableModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={() => {
            setIsCreateModalOpen(false);
            fetchTables();
          }}
          carId={selectedCar ? Number(selectedCar) : (cars.length > 0 ? cars[0].id : 1)}
          table={null}
          isEdit={false}
        />
      )}

      {/* Модальное окно редактирования */}
      {editingTable && (
        <CalibrationTableModal
          isOpen={!!editingTable}
          onClose={closeEditModal}
          onSuccess={() => {
            closeEditModal();
            fetchTables();
          }}
          carId={editingTable.carId}
          table={editingTable}
          isEdit={true}
        />
      )}
    </div>
  );
};

export default CalibrationTableManagement; 